// The size budget for chats no mounted surface retains, in parts. The browser
// has no cheap byte measure, but the server bounds what a part carries: earlier
// turns arrive as cards that leave out each field over 1 KiB, and only the
// latest two turns carry strings up to 50 KiB (backend/transcript/project.go).
// A chat released by its last surface keeps its latest window of 60 messages,
// a few hundred parts (an assistant step is a step start and finish around its
// text, reasoning, and tool parts), so this holds a few dozen background chats
// across every workspace. Each chat also costs one unit for being tracked.
const SESSION_CACHE_PART_BUDGET = 10_000

type Entry = { key: string; directory: string; sessionID: string }

export function createSessionCacheLifecycleController(input: {
  scope: () => string
  /** Parts the chat holds in memory. */
  size(directory: string, sessionID: string): number
  budget?: number
  onEvict(directory: string, sessionIDs: string[]): void
  /** Called after the last surface retaining a chat releases it. */
  onRelease?(directory: string, sessionID: string): void
  /** Called when the sessions it tracks or retains change. */
  onChange?: () => void
}) {
  // Every tracked chat on every machine, least recently used first.
  const seen = new Map<string, Entry>()
  const missing = new Set<string>()
  const retained = new Map<string, Map<string, number>>()
  const budget = input.budget ?? SESSION_CACHE_PART_BUDGET
  const maxMissing = 500
  let disposed = false
  const directoryKey = (directory: string) => `${input.scope()}\n${directory}`
  const sessionKey = (directory: string, sessionID: string) =>
    `${directoryKey(directory)}\n${sessionID}`

  const evict = (entries: Entry[]) => {
    const groups = new Map<string, { directory: string; sessionIDs: string[] }>()
    for (const entry of entries) {
      let group = groups.get(entry.key)
      if (!group) groups.set(entry.key, (group = { directory: entry.directory, sessionIDs: [] }))
      group.sessionIDs.push(entry.sessionID)
    }
    for (const group of groups.values()) input.onEvict(group.directory, group.sessionIDs)
  }

  // Evicts the least recently used chats no surface retains until the rest fit
  // the budget. `keep` is the chat being opened, which is never evicted.
  const enforceBudget = (keep?: string) => {
    let total = 0
    const candidates: Array<[string, Entry, number]> = []
    for (const [id, entry] of seen) {
      if (retained.get(entry.key)?.has(entry.sessionID)) continue
      const cost = 1 + input.size(entry.directory, entry.sessionID)
      total += cost
      if (id !== keep) candidates.push([id, entry, cost])
    }
    const stale: Entry[] = []
    for (const [id, entry, cost] of candidates) {
      if (total <= budget) break
      seen.delete(id)
      stale.push(entry)
      total -= cost
    }
    evict(stale)
    return stale.length > 0
  }

  const touch = (directory: string, sessionID: string) => {
    if (disposed) return
    const id = sessionKey(directory, sessionID)
    const added = !seen.delete(id)
    seen.set(id, { key: directoryKey(directory), directory, sessionID })
    if (enforceBudget(id) || added) input.onChange?.()
  }

  const retainedSessions = () => {
    const prefix = directoryKey("")
    const sessions: Array<{ directory: string; sessionID: string }> = []
    for (const [key, owners] of retained) {
      if (!key.startsWith(prefix)) continue
      const directory = key.slice(prefix.length)
      for (const sessionID of owners.keys()) sessions.push({ directory, sessionID })
    }
    return sessions
  }

  return {
    retain(directory: string, sessionID: string) {
      if (disposed) return () => {}
      const key = directoryKey(directory)
      let sessions = retained.get(key)
      if (!sessions) retained.set(key, sessions = new Map())
      const count = sessions.get(sessionID) ?? 0
      sessions.set(sessionID, count + 1)
      touch(directory, sessionID)
      if (count === 0) input.onChange?.()
      let released = false
      return () => {
        if (released) return
        released = true
        const count = sessions.get(sessionID) ?? 0
        if (count > 1) sessions.set(sessionID, count - 1)
        else sessions.delete(sessionID)
        if (!sessions.size && retained.get(key) === sessions) retained.delete(key)
        if (count !== 1 || disposed) return
        input.onChange?.()
        // A surface replaced by another showing the same chat, such as a page
        // becoming a grid tile, releases it before the new one retains it.
        queueMicrotask(() => {
          if (disposed || retained.get(key)?.has(sessionID)) return
          input.onRelease?.(directory, sessionID)
          if (enforceBudget()) input.onChange?.()
        })
      }
    },
    retained(directory: string, sessionID: string) {
      return !!retained.get(directoryKey(directory))?.has(sessionID)
    },
    /** Every session a mounted surface retains on the current machine. */
    retainedSessions,
    /** Every session it tracks or retains on the current machine. */
    trackedSessions() {
      const prefix = directoryKey("")
      const sessions = new Map<string, { directory: string; sessionID: string; retained: boolean }>()
      for (const { key, directory, sessionID } of seen.values()) {
        if (key.startsWith(prefix)) sessions.set(sessionID, { directory, sessionID, retained: false })
      }
      for (const { directory, sessionID } of retainedSessions()) sessions.set(sessionID, { directory, sessionID, retained: true })
      return [...sessions.values()]
    },
    tracked(directory: string, sessionID: string) {
      return !disposed && seen.has(sessionKey(directory, sessionID))
    },
    touch,
    remove(directory: string, sessionID: string) {
      if (seen.delete(sessionKey(directory, sessionID))) input.onChange?.()
    },
    clearDirectory(directory: string) {
      const key = directoryKey(directory)
      const sessions: string[] = []
      for (const [id, entry] of seen) {
        if (entry.key !== key) continue
        seen.delete(id)
        missing.delete(id)
        sessions.push(entry.sessionID)
      }
      input.onEvict(directory, sessions)
      if (sessions.length) input.onChange?.()
    },
    isMissing(directory: string, sessionID: string) {
      return missing.has(sessionKey(directory, sessionID))
    },
    markMissing(directory: string, sessionID: string) {
      const id = sessionKey(directory, sessionID)
      if (missing.has(id)) return false
      missing.add(id)
      while (missing.size > maxMissing) {
        const stale = missing.values().next().value
        if (!stale) break
        missing.delete(stale)
      }
      if (seen.delete(id)) input.onChange?.()
      return true
    },
    restore(directory: string, sessionID: string) {
      missing.delete(sessionKey(directory, sessionID))
    },
    dispose() {
      disposed = true
      const entries = [...seen.values()]
      seen.clear()
      missing.clear()
      retained.clear()
      evict(entries)
    },
  }
}
